<template>
  <m-content
    class="invite-users"
    padding
    :padding-top="0"
  >
    <div class="_description">
      {{ $t('inviteUsers.description') }}
    </div>
    <div class="_item">
      <div class="_label">
        {{ $t('inviteUsers.inviteUsersLabel') }}
      </div>
      <m-tooltip :disabled="!nextWouldExceedUserLimit">
        <div class="_email-input">
          <m-input
            v-model:value="email"
            full-width
            class="_input"
            :placeholder="$t('inviteUsers.inviteUsersPlaceholder')"
            :has-error="inputError !== ''"
            @focus="emailFocused = true"
            @blur="emailFocused = false"
            @enter="addEmail"
          />
          <m-btn
            color="primary"
            :disabled="nextWouldExceedUserLimit"
            @click="addEmail"
          >
            {{ $t('inviteUsers.addEmail') }}
          </m-btn>
        </div>
        <template #title>
          {{ $t('userList.trialUserLimitReached', { limit: userLimitValue }) }}
        </template>
      </m-tooltip>
      <div
        v-if="inputError !== ''"
        class="_error-message"
      >
        {{ inputError }}
      </div>
      <div
        v-if="emails.length > 0"
        class="_email-list"
      >
        <m-tag
          v-for="e in emails"
          :key="e"
          :title="e"
          closeable
          @close="removeEmail(e)"
        />
      </div>
    </div>
    <div class="_item">
      <div class="_label">
        {{ $t('inviteUsers.spaceLabel') }}
      </div>
      <space-selector
        v-model:value="selectedSpaces"
        class="_select"
        :property-label="spaceProperty.label"
        :truncate="5"
        :with-archived="false"
        :placeholder="$t('inviteUsers.spacePlaceholder')"
        placement="onTopLeft"
        match-trigger-width
        full-width
        multiple
        wrap
      />
    </div>
    <m-alert
      v-if="userLimitReached"
      type="info"
      :message="$t('userList.trialUserLimitReached', { limit: userLimitValue })"
    />
    <div class="_actions">
      <m-btn
        block
        :loading="loading"
        :disabled="emails.length === 0 || wouldExceedUserLimit"
        color="primary"
        @click="sendInvites"
      >
        {{ $t('inviteUsers.sendInvite') }}
      </m-btn>
      <m-btn
        full-width
        @click="emit('close')"
      >
        {{ $t('general.cancel') }}
      </m-btn>
    </div>
  </m-content>
</template>

<script setup>
import SpaceSelector from '@/components/spaces/SpaceSelector.vue';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useInvitations from '@/composables/user/invitations/invitations';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useOnboardingMarker from '@/composables/onboarding/onboarding-marker';
import useProperties from '@/composables/property/property';
import useSnackbar from '@/composables/snackbar';
import useUserLimit from '@/composables/user/user-limit';
import useUsers from '@/composables/user/users';
import { computed, ref, watch } from 'vue';
import { emailRegex } from 'shared/lib/email-regex';
import { onboardingGuideSteps } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackbar = useSnackbar();
const emit = defineEmits(['close']);

const { loggedInUserAccount } = useLoggedInUserAccount();
const { loggedInUser } = useLoggedInUser();
const accountSettingsSvc = useAccountSettings();
const { spaceProperty } = useProperties();
const { markAsDone } = useOnboardingMarker(loggedInUserAccount, accountSettingsSvc);
const { createUsers } = useUsers();
const { inviteUsers } = useInvitations();
const { nonDemoUserCount, userLimitValue, userLimitReached } = useUserLimit();

const email = ref('');
const emails = ref([]);
const selectedSpaces = ref([]);
const loading = ref(false);
const emailFocused = ref(false);
const submitPressed = ref(false);

const isValidEmail = computed(() => {
  const rg = new RegExp(emailRegex());
  return rg.test(email.value);
});

const alreadyAdded = computed(() => emails.value.includes(email.value));

const errorMessage = computed(() => {
  if (!isValidEmail.value) {
    return t('inviteUsers.invalidEmail');
  }
  if (alreadyAdded.value) {
    return t('inviteUsers.alreadyAdded');
  }
  return '';
});

const inputError = computed(() => {
  if (!submitPressed.value && (email.value === '' || emailFocused.value)) {
    return '';
  }
  return errorMessage.value;
});

watch(email, () => {
  submitPressed.value = false;
});

const addEmail = () => {
  if (nextWouldExceedUserLimit.value) {
    return;
  }
  submitPressed.value = true;
  if (!isValidEmail.value || alreadyAdded.value) {
    return;
  }
  emails.value = [...emails.value, email.value];
  email.value = '';
};

const removeEmail = (emailToRemove) => {
  emails.value = emails.value.filter((e) => e !== emailToRemove);
};

const nextWouldExceedUserLimit = computed(() => userLimitValue.value !== undefined
  && nonDemoUserCount.value + emails.value.length + 1 > userLimitValue.value);

const wouldExceedUserLimit = computed(() => userLimitValue.value !== undefined
  && nonDemoUserCount.value + emails.value.length > userLimitValue.value);

const parseDuplicateEmailError = (message) => {
  const result = [];
  const lines = message.split('\n');
  lines.forEach((line) => {
    const s1 = 'user with email ';
    const s2 = ' already exists';
    const i1 = line.indexOf(s1);
    const i2 = line.indexOf(s2);
    if (i1 > -1 && i2 > -1) {
      result.push(line.substring(i1 + s1.length, i2));
    }
  });
  return result;
};

const sendInvites = () => {
  if (wouldExceedUserLimit.value) {
    snackbar.error(t('userList.trialUserLimitReached', { limit: userLimitValue.value }));
    return;
  }
  loading.value = true;
  const toCreate = emails.value.map((e) => ({
    email: e,
    language: loggedInUser.value.language,
    values: [
      {
        property: { uid: spaceProperty.value.uid },
        spaces: selectedSpaces.value.map((o) => ({ uid: o.uid })),
      },
    ],
  }));
  createUsers(toCreate)
    .then((createdUsers) => inviteUsers(createdUsers))
    .then(() => markAsDone([onboardingGuideSteps.inviteOthers]))
    .then(() => {
      snackbar.success(t('inviteUsers.success'));
      emit('close');
    })
    .catch((e) => {
      if (e.message.includes('already exists')) {
        const duplicateEmails = parseDuplicateEmailError(e.message);
        snackbar.error(t('inviteUsers.oneEmailAlreadyExists'), { description: `- ${duplicateEmails.join(', ')}`, duration: 30 });
      } else {
        snackbar.error();
      }
      loading.value = false;
    });
};
</script>

<style scoped lang="scss" type="text/scss">
  .invite-users {
    ._description {
      color: $font-color-secondary;
      margin-bottom: 1.6rem;
    }

    ._item {
      margin-bottom: 1.6rem;

      ._label {
        color: $font-color-secondary;
        margin-bottom: .4rem;
      }
    }

    ._email-input {
      display: flex;
      gap: .8rem;

      ._input {
        flex: 1;
      }
    }

    ._error-message {
      color: $error-color;
      margin-top: .4rem;
    }

    ._email-list {
      display: flex;
      flex-wrap: wrap;
      gap: .4rem;
      max-height: 12rem;
      margin-top: .8rem;
      overflow-y: auto;
    }

    ._actions {
      display: flex;
      flex-direction: column;
      gap: .8rem;
      margin-top: 2rem;
    }
  }
</style>
